import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Landmark,
  Wallet2,
  CalendarDays,
  BarChart2,
  MoreHorizontal,
  X,
  Building,
  Receipt,
} from 'lucide-react';
import { useState } from 'react';

// Primary tabs shown in the bottom bar (max 5 for clean mobile UX)
const PRIMARY_TABS = [
  { name: 'Home',     icon: LayoutDashboard, path: '/home' },
  { name: 'Accounts', icon: Landmark,        path: '/bank-accounts' },
  { name: 'Spending', icon: Wallet2,         path: '/spending' },
  { name: 'Forecast', icon: CalendarDays,    path: '/forecast' },
  { name: 'More',     icon: MoreHorizontal,  path: '__more__' },
];

// Items hidden behind the "More" menu
const MORE_ITEMS = [
  { name: 'My Debts',       icon: Building,  path: '/my-debts' },
  { name: 'Subscriptions',  icon: Receipt,   path: '/subscriptions' },
  { name: 'Analytics',      icon: BarChart2,  path: '/analytics' },
];

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isMoreActive = MORE_ITEMS.some((item) => isActive(item.path));

  const handleTap = (path: string) => {
    if (path === '__more__') {
      setMoreOpen((o) => !o);
      return;
    }
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* More menu overlay */}
      {moreOpen && (
        <div className="fixed inset-0 z-[89] lg:hidden" onClick={() => setMoreOpen(false)} />
      )}

      {/* More menu popover */}
      {moreOpen && (
        <div className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+68px)] right-3 z-[91] lg:hidden animate-slide-up">
          <div className="glass-card rounded-2xl p-2 border border-white/10 shadow-2xl min-w-[180px]"
               style={{ background: 'rgba(19,19,31,0.97)' }}>
            <div className="flex items-center justify-between px-3 py-2 mb-1">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">More</span>
              <button
                onClick={() => setMoreOpen(false)}
                className="p-1 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {MORE_ITEMS.map((item) => (
              <button
                key={item.path}
                onClick={() => handleTap(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom tab bar */}
      <nav
        id="bottom-nav"
        className="fixed bottom-0 left-0 right-0 z-[90] lg:hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(15,15,19,0.92) 0%, rgba(15,15,19,0.98) 100%)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <div className="flex items-center justify-around h-[60px] max-w-lg mx-auto px-1">
          {PRIMARY_TABS.map((tab) => {
            const active = tab.path === '__more__' ? isMoreActive || moreOpen : isActive(tab.path);

            return (
              <button
                key={tab.path}
                id={`btm-nav-${tab.name.toLowerCase()}`}
                onClick={() => handleTap(tab.path)}
                className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1 transition-all duration-200 group"
              >
                {/* Active indicator dot */}
                <div className={`w-1 h-1 rounded-full mb-0.5 transition-all duration-300 ${
                  active ? 'bg-indigo-400 scale-100' : 'bg-transparent scale-0'
                }`} />
                <tab.icon
                  className={`h-[20px] w-[20px] transition-all duration-200 ${
                    active
                      ? 'text-indigo-400 scale-110'
                      : 'text-gray-500 group-active:scale-90'
                  }`}
                />
                <span
                  className={`text-[10px] font-medium leading-tight transition-colors duration-200 ${
                    active ? 'text-indigo-400' : 'text-gray-600'
                  }`}
                >
                  {tab.name}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
